import { useEffect, useMemo, useState } from "react";
import { useProjectStore } from "../../store/useProjectStore";
import { Loader2, Download, X, Wallet, BarChart3, Search } from "lucide-react";

const money = (n: any) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const resourceTotal = (p: any) =>
  (p?.resourceCosts || []).reduce(
    (sum: number, r: any) => sum + (Number(r?.amount) || 0),
    0
  );

const statusPill = (status?: string) => {
  const base =
    "inline-flex items-center justify-center px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap";
  switch (status) {
    case "Completed":
      return `${base} bg-emerald-100 text-emerald-700`;
    case "In Progress":
      return `${base} bg-blue-100 text-blue-700`;
    case "Cancelled":
      return `${base} bg-red-100 text-red-700`;
    default:
      return `${base} bg-gray-100 text-gray-700`;
  }
};

const FinanceProjectsView = () => {
  const { projects, fetchProjects, isFetchingProjects } = useProjectStore();
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [detail, setDetail] = useState<any>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const filtered = useMemo(() => {
    return projects.filter((p: any) => {
      if (statusFilter && p.projectStatus !== statusFilter) return false;
      if (typeFilter && p.projectType !== typeFilter) return false;
      if (
        searchTerm &&
        !(p.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;
      return true;
    });
  }, [projects, statusFilter, typeFilter, searchTerm]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc: any, p: any) => {
        acc.price += Number(p.projectPrice) || 0;
        acc.escrow += Number(p.escrowAmount) || 0;
        acc.released += Number(p.releasedPayment) || 0;
        acc.cost += resourceTotal(p);
        return acc;
      },
      { price: 0, escrow: 0, released: 0, cost: 0 }
    );
  }, [filtered]);

  // Export only finance-relevant columns. No client-identifiable fields.
  const exportCsv = () => {
    const headers = [
      "Project ID",
      "Project Name",
      "Category",
      "Status",
      "Payment Status",
      "Project Price",
      "Escrow Amount",
      "Released Payment",
      "Resource Cost",
      "Logged Hours",
    ];
    const rows = filtered.map((p: any) => [
      p._id,
      p.name,
      p.projectType || "",
      p.projectStatus || "",
      p.paymentStatus || "",
      p.projectPrice ?? "",
      p.escrowAmount ?? "",
      p.releasedPayment ?? "",
      resourceTotal(p),
      p.loggedHours ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `project-financials-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const types = useMemo(
    () => Array.from(new Set(projects.map((p: any) => p.projectType).filter(Boolean))),
    [projects]
  );

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              Project Financials
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Financial review &amp; reporting · client details are not shown
            </p>
          </div>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white font-semibold rounded-xl px-5 py-2.5 shadow-md hover:shadow-lg transition disabled:opacity-50"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Revenue", value: money(totals.price), icon: Wallet },
            { label: "In Escrow", value: money(totals.escrow), icon: BarChart3 },
            { label: "Released", value: money(totals.released), icon: BarChart3 },
            { label: "Resource Cost", value: money(totals.cost), icon: BarChart3 },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
              >
                <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <Icon size={14} /> {c.label}
                </div>
                <div className="text-xl font-bold text-slate-900 mt-2">
                  {c.value}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 lg:p-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 min-w-[200px]">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search project name…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
            >
              <option value="">All Statuses</option>
              {["Not Started", "In Progress", "On Hold", "Completed", "Cancelled"].map(
                (s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                )
              )}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
            >
              <option value="">All Categories</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {isFetchingProjects ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <Loader2 className="animate-spin text-[#2462b2]" size={36} />
              <p className="text-gray-500 font-medium">Loading financials…</p>
            </div>
          ) : !filtered.length ? (
            <div className="text-center py-16 text-slate-500">
              No projects match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm text-left text-gray-700">
                <thead className="bg-[#2462b2]/5 text-xs font-bold uppercase tracking-wider text-gray-700 border-b border-gray-200">
                  <tr>
                    {[
                      "Project Name",
                      "Category",
                      "Status",
                      "ETA",
                      "Project Price",
                      "Escrow",
                      "Released",
                      "Payment",
                      "Resource Cost",
                      "Logged Hrs",
                      "",
                    ].map((h) => (
                      <th key={h} className="px-4 py-3 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((p: any) => (
                    <tr key={p._id} className="hover:bg-[#2462b2]/[0.03] transition">
                      <td className="px-4 py-3 font-semibold text-gray-900">
                        {p.name}
                      </td>
                      <td className="px-4 py-3">{p.projectType || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={statusPill(p.projectStatus)}>
                          {p.projectStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {p.eta ? new Date(p.eta).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">{money(p.projectPrice)}</td>
                      <td className="px-4 py-3">{money(p.escrowAmount)}</td>
                      <td className="px-4 py-3">{money(p.releasedPayment)}</td>
                      <td className="px-4 py-3">{p.paymentStatus || "—"}</td>
                      <td className="px-4 py-3">{money(resourceTotal(p))}</td>
                      <td className="px-4 py-3">{p.loggedHours ?? "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setDetail(p)}
                          className="text-[#2462b2] font-semibold hover:underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {detail && (
        <FinanceProjectDetail project={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

const FinanceProjectDetail = ({
  project,
  onClose,
}: {
  project: any;
  onClose: () => void;
}) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{project.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {project.projectType} · {project.projectStatus}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
              Financial Summary
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Project Price", money(project.projectPrice)],
                ["Source", project.source || "Upwork"],
                ["Payment Status", project.paymentStatus || "—"],
                ...(project.source === "Direct"
                  ? [
                      ["Upfront", money(project.upfrontPayment)],
                      ["Payment Method", project.paymentMethod || "—"],
                      ["Amount Received", money(project.releasedPayment)],
                    ]
                  : [
                      ["Escrow Amount", money(project.escrowAmount)],
                      ["Released Payment", money(project.releasedPayment)],
                    ]),
                ["Logged Hours", project.loggedHours ?? "—"],
                ["Resource Cost", money(resourceTotal(project))],
              ].map(([label, value]) => (
                <div
                  key={label as string}
                  className="bg-slate-50 rounded-lg p-3 border border-slate-100"
                >
                  <p className="text-[11px] text-slate-500 font-semibold uppercase">
                    {label}
                  </p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{value}</p>
                </div>
              ))}
            </div>
          </section>

          {!!(project.resourceCosts || []).length && (
            <section>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
                Resource Allocation Costs
              </h3>
              <div className="rounded-lg border border-slate-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Label</th>
                      <th className="px-3 py-2 text-left font-semibold">Type</th>
                      <th className="px-3 py-2 text-left font-semibold">Amount</th>
                      <th className="px-3 py-2 text-left font-semibold">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {project.resourceCosts.map((r: any, i: number) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{r.label}</td>
                        <td className="px-3 py-2">{r.type}</td>
                        <td className="px-3 py-2">{money(r.amount)}</td>
                        <td className="px-3 py-2">{r.hours || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {!!(project.milestones || []).length && (
            <section>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
                Milestones
              </h3>
              <div className="rounded-lg border border-slate-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Title</th>
                      <th className="px-3 py-2 text-left font-semibold">Amount</th>
                      <th className="px-3 py-2 text-left font-semibold">Status</th>
                      <th className="px-3 py-2 text-left font-semibold">Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {project.milestones.map((m: any, i: number) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{m.title}</td>
                        <td className="px-3 py-2">{money(m.amount)}</td>
                        <td className="px-3 py-2">{m.status}</td>
                        <td className="px-3 py-2">
                          {m.dueDate
                            ? new Date(m.dueDate).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceProjectsView;
